/**
 * Life Blueprint V2.9.9 — M02 ReferenceData · Controlled Batch 02
 *   DB_藏干 (Sheet42, A1:F29 — 28 audited rows)
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * STATUS
 *   • DB_藏干 dataset status = AUTHORITATIVE_DATASET_COMPLETE (28 rows)
 *   • M02 overall: IN_PROGRESS, referenceCoverage = PARTIAL
 *     (Batch 01 = DB_天干, DB_地支; Batch 02 = DB_藏干. Remaining
 *     DB tables — Sheet39 partial, Sheet43, Sheet45, Sheet47, and any
 *     others required by later batches — NOT_STARTED.)
 *
 * ISOLATION
 *   Does NOT write into `src/engine/referenceData.js`. Production
 *   ReferenceData registry continues to hold only MOCK_POC placeholders.
 *   Does NOT wire into the main V2.9.9 pipeline.
 *   Does NOT modify M02 Batch 01 behaviour — reuses no export from
 *   `m02_batch01_reference.js` other than the cross-check import used
 *   by the validation runner.
 *
 * UNICODE POLICY
 *   Every Chinese literal is built from its audited code point via
 *   `String.fromCodePoint(...)`. No direct Chinese glyphs are used in
 *   code; a per-entry comment states the canonical glyph for reader
 *   convenience. This preserves MIGRATION_RULES.md → Encoding Warning.
 *
 * NUMERIC POLICY
 *   Excel 权重(%) values are stored as native JS `Number` (Excel Double).
 *   Values are the audited integer literals `10, 7, 6, 3, 1` — never
 *   divided, never rescaled. `typeof === "number"` is asserted by the
 *   validation runner for every row.
 *
 * LOOKUP CONTRACT
 *   Exact-match Unicode-safe equality on the composite key
 *   `<地支>_<类型>`. Unknown / empty keys throw
 *   `MISSING_REFERENCE_KEY` (the M02 Batch 01 convention). No fuzzy
 *   match, no transliteration, no alias substitution, no silent fallback.
 *   A grouped-by-地支 helper is provided but only as a deterministic
 *   VIEW over the exact 28-row dataset — it never manufactures missing
 *   本气 / 中气 / 余气 records.
 * ─────────────────────────────────────────────────────────────────────────────
 */

// ── Locally defined Unicode-safe character constants ────────────────────────
// (Duplicated from Batch 01 constants deliberately: keeping them local
// avoids the need to modify Batch 01 exports, and this file is the sole
// dependency-free representation of the audited DB_藏干 data.)

// 地支 (12 branches)
const CH_ZI     = String.fromCodePoint(0x5B50); // 子
const CH_CHOU   = String.fromCodePoint(0x4E11); // 丑
const CH_YIN    = String.fromCodePoint(0x5BC5); // 寅
const CH_MAO    = String.fromCodePoint(0x536F); // 卯
const CH_CHEN   = String.fromCodePoint(0x8FB0); // 辰
const CH_SI     = String.fromCodePoint(0x5DF3); // 巳
const CH_WU_BR  = String.fromCodePoint(0x5348); // 午
const CH_WEI    = String.fromCodePoint(0x672A); // 未
const CH_SHEN   = String.fromCodePoint(0x7533); // 申
const CH_YOU    = String.fromCodePoint(0x9149); // 酉
const CH_XU     = String.fromCodePoint(0x620C); // 戌
const CH_HAI    = String.fromCodePoint(0x4EA5); // 亥

// 天干 (10 stems, used as 藏干 values)
const CH_JIA  = String.fromCodePoint(0x7532); // 甲
const CH_YI   = String.fromCodePoint(0x4E59); // 乙
const CH_BING = String.fromCodePoint(0x4E19); // 丙
const CH_DING = String.fromCodePoint(0x4E01); // 丁
const CH_WU   = String.fromCodePoint(0x620A); // 戊
const CH_JI   = String.fromCodePoint(0x5DF1); // 己
const CH_GENG = String.fromCodePoint(0x5E9A); // 庚
const CH_XIN  = String.fromCodePoint(0x8F9B); // 辛
const CH_REN  = String.fromCodePoint(0x58EC); // 壬
const CH_GUI  = String.fromCodePoint(0x7678); // 癸

// 五行 (five elements)
const EL_WOOD  = String.fromCodePoint(0x6728); // 木
const EL_FIRE  = String.fromCodePoint(0x706B); // 火
const EL_EARTH = String.fromCodePoint(0x571F); // 土
const EL_METAL = String.fromCodePoint(0x91D1); // 金
const EL_WATER = String.fromCodePoint(0x6C34); // 水

// 类型 — the ONLY three audited 类型 values
export const CANG_GAN_TYPE = Object.freeze({
  BEN_QI:  String.fromCodePoint(0x672C, 0x6C14), // 本气
  ZHONG_QI: String.fromCodePoint(0x4E2D, 0x6C14), // 中气
  YU_QI:   String.fromCodePoint(0x4F59, 0x6C14), // 余气
});

// Composite-key separator (audited ASCII underscore U+005F).
const KEY_SEP = "_";

/** Compose the audited composite key `<地支> "_" <类型>`. */
function ck(diZhi, type) {
  return diZhi + KEY_SEP + type;
}

// ── Headers (audited Unicode) ────────────────────────────────────────────────
// Kept as data so provenance is inspectable at runtime.
const HEADER_COMPOSITE_KEY = String.fromCodePoint(
  0x590D, 0x5408, 0x952E, 0x28, 0x5730, 0x652F, 0x5F, 0x7C7B, 0x578B, 0x29
); // 复合键(地支_类型)
const HEADER_DI_ZHI = String.fromCodePoint(0x5730, 0x652F); // 地支
const HEADER_LEI_XING = String.fromCodePoint(0x7C7B, 0x578B); // 类型
const HEADER_CANG_GAN = String.fromCodePoint(0x85CF, 0x5E72); // 藏干
const HEADER_CANG_GAN_WU_XING = String.fromCodePoint(
  0x85CF, 0x5E72, 0x4E94, 0x884C
); // 藏干五行
const HEADER_QUAN_ZHONG = String.fromCodePoint(
  0x6743, 0x91CD, 0x28, 0x25, 0x29
); // 权重(%)

// ── Dataset ──────────────────────────────────────────────────────────────────

/**
 * DB_藏干 — 28 audited rows, ordered as in the Excel Golden Master
 * (Sheet42 rows 2..29). Frozen at row and object level.
 */
export const DB_CANG_GAN = Object.freeze({
  codeName: "Sheet42",
  worksheetIndex: 42,
  sheetName: String.fromCodePoint(0x44, 0x42, 0x5F, 0x85CF, 0x5E72), // DB_藏干
  sheetNameUnicode: [68, 66, 95, 34255, 24178],
  excelUsedRange: "A1:F29",
  status: "AUTHORITATIVE_DATASET_COMPLETE",
  evidenceProvenance: "read-only Golden Master Value2 + Unicode extraction",
  auditedInBatch: "M02_BATCH_02",
  keySeparator: KEY_SEP,
  headers: Object.freeze({
    A: HEADER_COMPOSITE_KEY,   // 复合键(地支_类型)
    B: HEADER_DI_ZHI,          // 地支
    C: HEADER_LEI_XING,        // 类型
    D: HEADER_CANG_GAN,        // 藏干
    E: HEADER_CANG_GAN_WU_XING, // 藏干五行
    F: HEADER_QUAN_ZHONG,      // 权重(%)
  }),
  types: CANG_GAN_TYPE,
  rows: Object.freeze([
    // 子 (1 row)
    Object.freeze({ compositeKey: ck(CH_ZI,   CANG_GAN_TYPE.BEN_QI),   diZhi: CH_ZI,   leiXing: CANG_GAN_TYPE.BEN_QI,   cangGan: CH_GUI,  cangGanWuXing: EL_WATER, quanZhong: 10 }),
    // 丑 (3 rows)
    Object.freeze({ compositeKey: ck(CH_CHOU, CANG_GAN_TYPE.BEN_QI),   diZhi: CH_CHOU, leiXing: CANG_GAN_TYPE.BEN_QI,   cangGan: CH_JI,   cangGanWuXing: EL_EARTH, quanZhong: 6  }),
    Object.freeze({ compositeKey: ck(CH_CHOU, CANG_GAN_TYPE.ZHONG_QI), diZhi: CH_CHOU, leiXing: CANG_GAN_TYPE.ZHONG_QI, cangGan: CH_GUI,  cangGanWuXing: EL_WATER, quanZhong: 3  }),
    Object.freeze({ compositeKey: ck(CH_CHOU, CANG_GAN_TYPE.YU_QI),    diZhi: CH_CHOU, leiXing: CANG_GAN_TYPE.YU_QI,    cangGan: CH_XIN,  cangGanWuXing: EL_METAL, quanZhong: 1  }),
    // 寅 (3 rows)
    Object.freeze({ compositeKey: ck(CH_YIN,  CANG_GAN_TYPE.BEN_QI),   diZhi: CH_YIN,  leiXing: CANG_GAN_TYPE.BEN_QI,   cangGan: CH_JIA,  cangGanWuXing: EL_WOOD,  quanZhong: 6  }),
    Object.freeze({ compositeKey: ck(CH_YIN,  CANG_GAN_TYPE.ZHONG_QI), diZhi: CH_YIN,  leiXing: CANG_GAN_TYPE.ZHONG_QI, cangGan: CH_BING, cangGanWuXing: EL_FIRE,  quanZhong: 3  }),
    Object.freeze({ compositeKey: ck(CH_YIN,  CANG_GAN_TYPE.YU_QI),    diZhi: CH_YIN,  leiXing: CANG_GAN_TYPE.YU_QI,    cangGan: CH_WU,   cangGanWuXing: EL_EARTH, quanZhong: 1  }),
    // 卯 (1 row)
    Object.freeze({ compositeKey: ck(CH_MAO,  CANG_GAN_TYPE.BEN_QI),   diZhi: CH_MAO,  leiXing: CANG_GAN_TYPE.BEN_QI,   cangGan: CH_YI,   cangGanWuXing: EL_WOOD,  quanZhong: 10 }),
    // 辰 (3 rows)
    Object.freeze({ compositeKey: ck(CH_CHEN, CANG_GAN_TYPE.BEN_QI),   diZhi: CH_CHEN, leiXing: CANG_GAN_TYPE.BEN_QI,   cangGan: CH_WU,   cangGanWuXing: EL_EARTH, quanZhong: 6  }),
    Object.freeze({ compositeKey: ck(CH_CHEN, CANG_GAN_TYPE.ZHONG_QI), diZhi: CH_CHEN, leiXing: CANG_GAN_TYPE.ZHONG_QI, cangGan: CH_YI,   cangGanWuXing: EL_WOOD,  quanZhong: 3  }),
    Object.freeze({ compositeKey: ck(CH_CHEN, CANG_GAN_TYPE.YU_QI),    diZhi: CH_CHEN, leiXing: CANG_GAN_TYPE.YU_QI,    cangGan: CH_GUI,  cangGanWuXing: EL_WATER, quanZhong: 1  }),
    // 巳 (3 rows)
    Object.freeze({ compositeKey: ck(CH_SI,   CANG_GAN_TYPE.BEN_QI),   diZhi: CH_SI,   leiXing: CANG_GAN_TYPE.BEN_QI,   cangGan: CH_BING, cangGanWuXing: EL_FIRE,  quanZhong: 6  }),
    Object.freeze({ compositeKey: ck(CH_SI,   CANG_GAN_TYPE.ZHONG_QI), diZhi: CH_SI,   leiXing: CANG_GAN_TYPE.ZHONG_QI, cangGan: CH_GENG, cangGanWuXing: EL_METAL, quanZhong: 3  }),
    Object.freeze({ compositeKey: ck(CH_SI,   CANG_GAN_TYPE.YU_QI),    diZhi: CH_SI,   leiXing: CANG_GAN_TYPE.YU_QI,    cangGan: CH_WU,   cangGanWuXing: EL_EARTH, quanZhong: 1  }),
    // 午 (2 rows — no 余气)
    Object.freeze({ compositeKey: ck(CH_WU_BR, CANG_GAN_TYPE.BEN_QI),   diZhi: CH_WU_BR, leiXing: CANG_GAN_TYPE.BEN_QI,   cangGan: CH_DING, cangGanWuXing: EL_FIRE,  quanZhong: 7  }),
    Object.freeze({ compositeKey: ck(CH_WU_BR, CANG_GAN_TYPE.ZHONG_QI), diZhi: CH_WU_BR, leiXing: CANG_GAN_TYPE.ZHONG_QI, cangGan: CH_JI,   cangGanWuXing: EL_EARTH, quanZhong: 3  }),
    // 未 (3 rows)
    Object.freeze({ compositeKey: ck(CH_WEI,  CANG_GAN_TYPE.BEN_QI),   diZhi: CH_WEI,  leiXing: CANG_GAN_TYPE.BEN_QI,   cangGan: CH_JI,   cangGanWuXing: EL_EARTH, quanZhong: 6  }),
    Object.freeze({ compositeKey: ck(CH_WEI,  CANG_GAN_TYPE.ZHONG_QI), diZhi: CH_WEI,  leiXing: CANG_GAN_TYPE.ZHONG_QI, cangGan: CH_DING, cangGanWuXing: EL_FIRE,  quanZhong: 3  }),
    Object.freeze({ compositeKey: ck(CH_WEI,  CANG_GAN_TYPE.YU_QI),    diZhi: CH_WEI,  leiXing: CANG_GAN_TYPE.YU_QI,    cangGan: CH_YI,   cangGanWuXing: EL_WOOD,  quanZhong: 1  }),
    // 申 (3 rows)
    Object.freeze({ compositeKey: ck(CH_SHEN, CANG_GAN_TYPE.BEN_QI),   diZhi: CH_SHEN, leiXing: CANG_GAN_TYPE.BEN_QI,   cangGan: CH_GENG, cangGanWuXing: EL_METAL, quanZhong: 6  }),
    Object.freeze({ compositeKey: ck(CH_SHEN, CANG_GAN_TYPE.ZHONG_QI), diZhi: CH_SHEN, leiXing: CANG_GAN_TYPE.ZHONG_QI, cangGan: CH_REN,  cangGanWuXing: EL_WATER, quanZhong: 3  }),
    Object.freeze({ compositeKey: ck(CH_SHEN, CANG_GAN_TYPE.YU_QI),    diZhi: CH_SHEN, leiXing: CANG_GAN_TYPE.YU_QI,    cangGan: CH_WU,   cangGanWuXing: EL_EARTH, quanZhong: 1  }),
    // 酉 (1 row)
    Object.freeze({ compositeKey: ck(CH_YOU,  CANG_GAN_TYPE.BEN_QI),   diZhi: CH_YOU,  leiXing: CANG_GAN_TYPE.BEN_QI,   cangGan: CH_XIN,  cangGanWuXing: EL_METAL, quanZhong: 10 }),
    // 戌 (3 rows)
    Object.freeze({ compositeKey: ck(CH_XU,   CANG_GAN_TYPE.BEN_QI),   diZhi: CH_XU,   leiXing: CANG_GAN_TYPE.BEN_QI,   cangGan: CH_WU,   cangGanWuXing: EL_EARTH, quanZhong: 6  }),
    Object.freeze({ compositeKey: ck(CH_XU,   CANG_GAN_TYPE.ZHONG_QI), diZhi: CH_XU,   leiXing: CANG_GAN_TYPE.ZHONG_QI, cangGan: CH_XIN,  cangGanWuXing: EL_METAL, quanZhong: 3  }),
    Object.freeze({ compositeKey: ck(CH_XU,   CANG_GAN_TYPE.YU_QI),    diZhi: CH_XU,   leiXing: CANG_GAN_TYPE.YU_QI,    cangGan: CH_DING, cangGanWuXing: EL_FIRE,  quanZhong: 1  }),
    // 亥 (2 rows — no 余气)
    Object.freeze({ compositeKey: ck(CH_HAI,  CANG_GAN_TYPE.BEN_QI),   diZhi: CH_HAI,  leiXing: CANG_GAN_TYPE.BEN_QI,   cangGan: CH_REN,  cangGanWuXing: EL_WATER, quanZhong: 7  }),
    Object.freeze({ compositeKey: ck(CH_HAI,  CANG_GAN_TYPE.ZHONG_QI), diZhi: CH_HAI,  leiXing: CANG_GAN_TYPE.ZHONG_QI, cangGan: CH_JIA,  cangGanWuXing: EL_WOOD,  quanZhong: 3  }),
  ]),
});

// ── Lookup API ───────────────────────────────────────────────────────────────

function throwMissingKey(column, key) {
  const err = new Error(
    `[M02·DB_藏干] composite key ${JSON.stringify(key)} not found in ` +
      "AUTHORITATIVE_DATASET_COMPLETE (28 audited rows). No fuzzy match " +
      "or substitution is performed."
  );
  err.code = "MISSING_REFERENCE_KEY";
  err.dataset = "DB_藏干";
  err.column = column;
  err.key = key;
  throw err;
}

/**
 * Exact-match composite-key lookup. Returns the full audited row (frozen)
 * or throws `MISSING_REFERENCE_KEY`.
 *
 * @param {string} compositeKey e.g. "巳_本气"
 * @returns {{compositeKey:string, diZhi:string, leiXing:string, cangGan:string, cangGanWuXing:string, quanZhong:number}}
 */
export function cangGanLookup(compositeKey) {
  if (typeof compositeKey !== "string") {
    throw new TypeError("[M02·DB_藏干] compositeKey must be a string");
  }
  const row = DB_CANG_GAN.rows.find((r) => r.compositeKey === compositeKey);
  if (!row) throwMissingKey("row", compositeKey);
  return row;
}

/** Convenience: lookup by `<地支>` and `<类型>` separately. */
export function cangGanLookupBy(diZhi, leiXing) {
  if (typeof diZhi !== "string" || typeof leiXing !== "string") {
    throw new TypeError("[M02·DB_藏干] diZhi and leiXing must both be strings");
  }
  return cangGanLookup(diZhi + KEY_SEP + leiXing);
}

/**
 * Deterministic VIEW over the exact 28-row dataset grouped by 地支.
 * Ordering within a group preserves the Excel row order (本气 → 中气 → 余气).
 * NEVER manufactures missing 类型 rows — 子/卯/酉 return a single-row group,
 * and 午/亥 return a two-row group.
 *
 * @param {string} diZhi
 * @returns {ReadonlyArray<Object>}
 */
export function cangGanRowsForDiZhi(diZhi) {
  if (typeof diZhi !== "string") {
    throw new TypeError("[M02·DB_藏干] diZhi must be a string");
  }
  const rows = DB_CANG_GAN.rows.filter((r) => r.diZhi === diZhi);
  if (rows.length === 0) throwMissingKey("groupBy地支", diZhi);
  return Object.freeze(rows.slice());
}
